import React from 'react';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import getBaseSweetAlert from './get-base-sweet-alert';
// import { ReactSweetAlertOptions } from 'sweetalert2-react-content';
import { TFunction } from 'i18next';
import { OpenAlertOptions, SweetAlertHookApi } from 'types';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    fontSize: '1.5rem',
    fontFamily: theme.typography.fontFamily,
    fontWeight: 100,
    marginTop: '0px',
    padding: `0 ${theme.spacing(2)}px`
  },
  icon: {
    marginBottom: theme.spacing(2)
  },
  container: {
    zIndex: theme.zIndex.modal + 1
  },
  content: {
    ...theme.typography.body1,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 300,
    fontSize: '1.125rem'
  },
  popup: {
    width: '30rem',
    borderRadius: '8px',
    paddingRight: 0,
    paddingLeft: 0,
    paddingBottom: theme.spacing(3.5)
  },
  text: {
    marginBottom: '10px'
  },
  confirmButton: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(21),
    backgroundColor: `${theme.palette.primary.main} !important`,
    borderRadius: '24px !important',
    padding: `${theme.spacing(1.375)}px ${theme.spacing(3.5)}px`,
    height: 43,
    outline: 'none !important'
  },
  cancelButton: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(21),
    backgroundColor: `${theme.palette.common.white} !important`,
    color: `${theme.palette.grey[800]} !important`,
    border: `solid 1px ${theme.palette.grey[500]} !important`,
    padding: `${theme.spacing(1.375)}px ${theme.spacing(3.5)}px`,
    borderRadius: '24px !important',
    height: 43
  },
  actions: {
    marginTop: 0
  },
  input: {
    margin: '0 auto !important',
    padding: 0,
    height: theme.typography.pxToRem(28),
    color: '#000000',
    paddingLeft: theme.typography.pxToRem(12),
    width: '70%',
    border: `1px solid #979797 !important`,
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem !important',
    fontFamily: 'futura-pt, sans-serif !important',
    lineHeight: '1.3125rem',
    '&:focus': {
      border: `1px solid #000000 !important`
    },
    '&:active': {
      border: `1px solid #000000 !important`
    }
  }
}));

const useModificationAlertContentStyles = makeStyles<Theme>((theme: Theme) => {
  return {
    inputLabel: {
      fontSize: '1.25rem',
      marginBottom: 0
    },
    listContainer: {
      padding: theme.spacing(2)
    },
    modificationList: {
      listStyle: 'none',
      margin: '0 auto',
      paddingLeft: 0,
      marginBottom: theme.spacing(2),
      '& > li': {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      }
    }
  };
});
export interface ModificationAlertContentProps {
  textItems?: string[];
  deleteText?: string;
}

export const ModificationAlertContent: React.FC<ModificationAlertContentProps> = ({
  deleteText,
  textItems
}) => {
  const classes = useModificationAlertContentStyles();
  const inputLabel = deleteText ? (
    <label className={classes.inputLabel} htmlFor="delete-class-input">
      {deleteText}
    </label>
  ) : null;

  if (textItems) {
    return (
      <>
        <div className={classes.listContainer}>
          <ul className={classes.modificationList}>
            {textItems.map(
              (item: string): React.ReactNode => (
                <li key={item}>
                  <b>{item}</b>
                </li>
              )
            )}
          </ul>
        </div>
        {inputLabel}
      </>
    );
  }
  return inputLabel;
};

// TODO type
const getDeleteOptions: any = (deleteText: string, t: TFunction) =>
  deleteText
    ? {
        input: 'text',
        inputAttributes: {
          id: 'delete-class-input'
        },
        onOpen: (): void => {
          const confirm = Swal.getConfirmButton();
          confirm.setAttribute('disabled', 'true');
          Swal.getInput().addEventListener('keyup', (event: KeyboardEvent) => {
            if (event) {
              if (event.target) {
                if ((event.target as HTMLInputElement).value === t('DELETE')) {
                  Swal.enableButtons();
                } else {
                  confirm.setAttribute('disabled', 'true');
                }
              }
            }
          });
        }
      }
    : { onOpen: (): void => Swal.getCancelButton().focus() };

const useModificationAlert = (): SweetAlertHookApi => {
  const classes = useStyles();
  const { t } = useTranslation('Classrooms');

  const SweetAlert = getBaseSweetAlert({
    icon: 'warning',
    showCancelButton: true,
    showConfirmButton: true,
    reverseButtons: true,
    customClass: {
      actions: classes.actions,
      title: classes.title,
      container: classes.container,
      content: classes.content,
      popup: classes.popup,
      icon: classes.icon,
      cancelButton: classes.cancelButton,
      confirmButton: classes.confirmButton,
      input: classes.input
    }
  });

  const open = (
    options: OpenAlertOptions = {} as OpenAlertOptions
  ): Promise<SweetAlertResult | void> => {
    const { deleteText, html, textItems, ...restOptions } = options;
    const deleteOptions = getDeleteOptions(deleteText, t);

    return SweetAlert.fire({
      ...restOptions,
      ...deleteOptions,
      html: html || <ModificationAlertContent textItems={textItems} />
    });
  };

  return {
    open
  };
};

export default useModificationAlert;
